import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { articles, channels, hotSearches, stats } from "@/mocks/gossip";

export default function HeroSection() {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [focused, setFocused] = useState(false);

  const results = useMemo(() => {
    const q = query.trim();
    if (!q) return { channels: [], articles: [] };
    return {
      channels: channels.filter((c) => c.name.includes(q) || c.intro.includes(q)).slice(0, 4),
      articles: articles.filter((a) => a.title.includes(q) || a.summary.includes(q)).slice(0, 4),
    };
  }, [query]);

  const hasResults = results.channels.length + results.articles.length > 0;

  const submitSearch = (value: string) => {
    const q = value.trim();
    if (!q) return;
    const firstArticle = articles.find((a) => a.title.includes(q) || a.summary.includes(q));
    if (firstArticle) {
      navigate(`/article/${firstArticle.id}`);
    } else {
      navigate("/channels");
    }
  };

  return (
    <section className="relative w-full overflow-hidden">
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=Warm%20amber%20and%20terracotta%20cinematic%20backdrop%20of%20empty%20theatre%20stage%20curtains%20and%20soft%20spotlights%2C%20artistic%20abstract%20editorial%20photography%2C%20rich%20brown%20tones%2C%20dramatic%20lighting%2C%20no%20text&width=1600&height=900&seq=kl-hero-bg&orientation=landscape"
          alt="看料爆料专区频道入口页面背景"
          title="看料爆料 - 看料爆料专区、版块、频道入口大全"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-foreground-950/85 via-foreground-950/70 to-foreground-950/90" />
      </div>

      <div className="relative w-full px-4 md:px-6 pt-32 md:pt-44 pb-16 md:pb-24">
        <div className="max-w-4xl mx-auto w-full text-center">
          <span className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-background-50/10 border border-background-50/25 text-background-50/90 text-xs md:text-sm backdrop-blur">
            <i className="ri-fire-fill text-accent-400" />
            每日整理 · 频道入口集中导航
          </span>

          <h1 className="mt-6 font-heading font-black text-3xl sm:text-4xl md:text-6xl leading-tight text-background-50">
            看料爆料专区
            <span className="block mt-2 text-accent-400">频道 · 版块 · 栏目入口大全</span>
          </h1>

          <p className="mt-6 text-sm md:text-lg leading-relaxed text-background-50/85 max-w-2xl mx-auto">
            打开这里就是看料专区，热门瓜料与爆料分类集中整理。想找哪类瓜点，按板块点进去就能快速翻到，不用在多个平台之间来回找。
          </p>

          <div className="mt-8 md:mt-10 relative max-w-2xl mx-auto">
            <form
              onSubmit={(e) => {
                e.preventDefault();
                submitSearch(query);
              }}
              className="flex items-center gap-2 p-2 rounded-full bg-background-50"
            >
              <span className="w-9 h-9 flex items-center justify-center text-foreground-500 shrink-0">
                <i className="ri-search-line" />
              </span>
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onFocus={() => setFocused(true)}
                onBlur={() => setTimeout(() => setFocused(false), 160)}
                placeholder="搜索频道、版块或瓜料关键词"
                aria-label="搜索频道、版块或瓜料关键词"
                className="flex-1 min-w-0 bg-transparent text-sm md:text-base text-foreground-950 placeholder:text-foreground-400 outline-none py-2"
              />
              <button
                type="submit"
                className="shrink-0 px-5 md:px-6 py-2.5 rounded-full text-sm font-medium whitespace-nowrap bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors cursor-pointer"
              >
                搜索
              </button>
            </form>

            {focused && query.trim() && (
              <div className="absolute top-full left-0 right-0 mt-2 rounded-lg bg-background-50 border border-background-200 overflow-hidden text-left z-20">
                {hasResults ? (
                  <div className="max-h-80 overflow-y-auto">
                    {results.channels.length > 0 && (
                      <div className="p-2">
                        <p className="px-3 py-1.5 text-xs text-foreground-500">相关频道入口</p>
                        {results.channels.map((c) => (
                          <button
                            key={c.slug}
                            type="button"
                            onClick={() => navigate("/channels")}
                            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-md hover:bg-background-100 transition-colors text-left cursor-pointer"
                          >
                            <span className="w-8 h-8 rounded-md flex items-center justify-center bg-primary-100 text-primary-600 shrink-0">
                              <i className={c.icon} />
                            </span>
                            <span className="text-sm text-foreground-900 truncate">{c.name}</span>
                          </button>
                        ))}
                      </div>
                    )}
                    {results.articles.length > 0 && (
                      <div className="p-2 border-t border-background-200">
                        <p className="px-3 py-1.5 text-xs text-foreground-500">相关爆料</p>
                        {results.articles.map((a) => (
                          <button
                            key={a.id}
                            type="button"
                            onClick={() => navigate(`/article/${a.id}`)}
                            className="w-full flex items-start gap-3 px-3 py-2.5 rounded-md hover:bg-background-100 transition-colors text-left cursor-pointer"
                          >
                            <span className="w-8 h-8 rounded-md flex items-center justify-center bg-secondary-100 text-secondary-900 shrink-0">
                              <i className="ri-newspaper-line" />
                            </span>
                            <span className="text-sm text-foreground-900 line-clamp-2">{a.title}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="px-4 py-5 text-sm text-foreground-500">没有找到匹配内容，试试别的关键词</p>
                )}
              </div>
            )}
          </div>

          <div className="mt-5 flex items-center justify-center gap-2 flex-wrap">
            <span className="text-xs text-background-50/70">热搜：</span>
            {hotSearches.map((word) => (
              <button
                key={word}
                type="button"
                onClick={() => setQuery(word)}
                className="px-3 py-1 rounded-full text-xs whitespace-nowrap bg-background-50/10 border border-background-50/25 text-background-50/90 hover:bg-background-50/20 transition-colors cursor-pointer"
              >
                {word}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="relative w-full px-4 md:px-6 pb-14 md:pb-20">
        <div className="max-w-5xl mx-auto grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-5">
          {stats.map((s) => (
            <div
              key={s.label}
              className="rounded-lg px-4 py-5 md:px-6 md:py-6 bg-background-50/10 border border-background-50/20 backdrop-blur text-center"
            >
              <p className="font-heading font-bold text-2xl md:text-4xl text-accent-400">
                {s.value}
                <span className="text-sm md:text-base ml-0.5 text-background-50/70">{s.suffix}</span>
              </p>
              <p className="mt-1.5 text-xs md:text-sm text-background-50/80">{s.label}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}